import { createAction, createAsyncThunk, nanoid } from '@reduxjs/toolkit';

import watchtimeApi from '../../services/watchtimeApi';
import {
  getUserUuid,
  isUserConnected,
} from '../../services/schibstedAccount.js';
import { ensureFetchDelay } from '../../helpers/fetch';
import { selectAssetById } from '../assets';
import { fetchSvpAssetById } from '../assets/actions';

import { selectAssetWatchtimeItem } from './selectors';

import type { AssetsState } from '../assets';
import type { Vendor } from '../../types';
import type { AssetWatchtime } from '../../services/watchtimeApi';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { WatchtimeState } from './index';

const getUserId = async () => {
  const [userUuid, isConnected] = await Promise.all([
    getUserUuid(),
    isUserConnected(),
  ]);

  return isConnected ? userUuid : undefined;
};

export const fetchWatchtimes = createAsyncThunk(
  'watchtime/fetch',
  async ({ assetIds, vendor }: { assetIds: SvpAssetId[]; vendor: Vendor }) => {
    const userUuid = await getUserId();

    if (!userUuid) {
      return [];
    }

    return watchtimeApi.getAssetWatchtimes({
      userUuid,
      assetIds,
      vendor,
    });
  }
);

export const fetchWatchtimeHistory = createAsyncThunk(
  'watchtime/history',
  ({ vendor }: { vendor: Vendor }) => {
    return ensureFetchDelay<AssetWatchtime[]>(async () => {
      const userUuid = await getUserId();

      if (!userUuid) {
        return [];
      }

      return watchtimeApi.getWatchtimeHistory({ userUuid, vendor });
    });
  }
);

export const saveAssetWatchtime = createAsyncThunk<
  AssetWatchtime | undefined,
  { assetId: SvpAssetId; vendor: Vendor },
  { state: { watchtime: WatchtimeState; assets: AssetsState } }
>('watchtime/save', async ({ assetId, vendor }, thunkAPI) => {
  const userUuid = await getUserId();

  if (!userUuid) {
    return;
  }

  const asset = selectAssetById(thunkAPI.getState(), vendor, assetId);
  const item = selectAssetWatchtimeItem(thunkAPI.getState(), assetId, vendor);

  if (!asset || !item) {
    return;
  }

  return await watchtimeApi.saveAssetWatchtime({
    userUuid,
    vendor,
    data: {
      time: item.time ?? 0,
      assetId: asset.id,
      updatedAt: item.updatedAt,
    },
  });
});

export const fetchSeriesWatchtimes = createAsyncThunk<
  { seriesId: SvpCategoryId; watchtime: AssetWatchtime }[],
  {
    seriesIds: SvpCategoryId[];
    vendor: Vendor;
  },
  { state: { watchtime: WatchtimeState; assets: AssetsState } }
>('watchtime/series', async ({ seriesIds, vendor }, thunkAPI) => {
  const userUuid = await getUserId();

  if (!userUuid) {
    return [];
  }

  const { items } = await watchtimeApi.getSeriesWatchtimes({
    userUuid,
    vendor,
    seriesIds,
  });

  await Promise.all(
    items.map(async ({ seriesId, watchtime }) => {
      const { assetId } = watchtime;

      if (!selectAssetById(thunkAPI.getState(), vendor, assetId)) {
        await thunkAPI
          .dispatch(fetchSvpAssetById({ assetId, vendor }))
          .unwrap();
      }

      thunkAPI.dispatch(
        fetchSeriesWatchtimes.fulfilled([{ seriesId, watchtime }], nanoid(), {
          seriesIds: [seriesId],
          vendor,
        })
      );
    })
  );

  return items;
});

export const upsertAssetWatchtime = createAction<{
  vendor: Vendor;
  data: Omit<AssetWatchtime, 'isWatched'>;
}>('watchtime/upsert');
